import DOWNLOAD_MONETIZED_GOALS from "../../../../constants/monetized-goals";
import Modal from "./modal";
import { sendYaMetrikaEvent } from "../../analytics";

// TODO: Вынести класс в appdater-helpers
/**
 * Базовый класс модального окна content-lock.
 * Управляет жизненным циклом модала, таймером обратного отсчёта и привязкой событий.
 * Подклассы задают {@link browserType} и {@link browserTitle} в конструкторе,
 * реализуют {@link updateContent} и {@link clearContent} для версионного рендеринга.
 */
class ContentLockModal {
  /**
   * @param {object} params
   * @param {string} params.elementId - DOM id контейнера модала
   * @param {object} params.contentLockSettings - Настройки из /api/download-settings (browserType, browserLink и т.д.)
   * @param {HTMLElement} params.buttonElem - Оригинальная кнопка скачивания (для копирования атрибутов)
   * @param {object} params.buttonAttributes - Атрибуты кнопки для аналитики
   */
  constructor({ elementId, contentLockSettings, buttonElem, buttonAttributes }) {
    this.container = document.getElementById(elementId);
    this.buttonElem = buttonElem;
    this.buttonAttributes = buttonAttributes;
    this.timerIds = [];

    this.browserType = contentLockSettings.browserType;
    this.browserLink = contentLockSettings.browserLink;

    this.browserTitle = this.browserType === "yandex" ? "Яндекс Браузер" : "Браузер Opera";
    this.termBrowserEl = this.container.querySelector("#term-browser");
    this.infoBlockListEl = this.container.querySelector("[data-id='info-block-list']");

    this.modalImageEl = this.container.querySelector("#lock-image");
    this.modalTitleEl = this.container.querySelector("#lock-name");
    this.modalLinkEl = this.container.querySelector("[data-id='browser-link']");
    this.toplineSecondEls = this.container.querySelectorAll("[data-id='second']");
    this.toplineBrowserEls = this.container.querySelectorAll("[data-id='browser']");
    this.toplineSoftLinkEls = this.container.querySelectorAll("[data-id='content-lock-soft-link']");
    this.closeModalButton = this.container.querySelector(".close-container");
    this.modalBackground = this.container.querySelector(".modal-background");

    this._boundClickDownloadFile = this._clickDownloadFile.bind(this);
    this._boundHandleClose = this._handleClose.bind(this);
    this._boundHandleDownloadBrowser = this._handleDownloadBrowser.bind(this);

    this.modal = new Modal({
      elementId,
      onOpenCallback: () => this._onOpen(),
      onCloseCallback: () => this._onClose(),
    });
  }

  updateContent() {
    this._updateCommonElements();

    this.termBrowserEl.textContent = this.browserTitle;
    this.infoBlockListEl.innerHTML = this.browserType === "yandex" ? `
      <li>Яндекс Браузер</li>
      <li>Настройки быстрого доступа к поиску и сервисам Яндекса</li>
      <li>Расширения Яндекса для браузеров</li>
    ` : `
      <li>Браузер Opera</li>
      <li>Бесплатный VPN</li>
      <li>Расширения для браузеров</li>
    `;
  }

  clearContent() {
    this._clearCommonElements();

    this.infoBlockListEl.textContent = "";
    this.termBrowserEl.textContent = "";
  }

  open() {
    this.modal.open();
  }

  _handleClose() {
    this.modal?.close();
  }

  _handleDownloadBrowser() {
    sendYaMetrikaEvent(DOWNLOAD_MONETIZED_GOALS.contentLockDownloadBrowser[this.browserType]);
  }

  _clickDownloadFile() {
    // целевое скачивание
    this.toplineSoftLinkEls[0].dispatchEvent(new Event("mouseenter"));
    this.toplineSoftLinkEls[0].click();
  }

  _onOpen() {
    this.goal = DOWNLOAD_MONETIZED_GOALS.contentLockInstallation[this.browserType];
    sendYaMetrikaEvent(DOWNLOAD_MONETIZED_GOALS.openContentLock[this.browserType]);

    this.updateContent();
    this._startTimer();
    this._bindEvents();
  }

  _onClose() {
    this._clearTimers();
    this._unbindEvents();
    this.clearContent();
  }

  _startTimer() {
    let callbackFired = false;
    Array.from(this.toplineSecondEls).forEach((el) => {
      let timeLeft = parseInt(el.textContent, 10) || 10;

      const timerId = setInterval(() => {
        timeLeft -= 1;
        // eslint-disable-next-line no-param-reassign
        el.textContent = timeLeft;

        if (timeLeft <= 0 && !callbackFired) {
          callbackFired = true;
          this._clearTimers();
          this.toplineSoftLinkEls.forEach(() => {
            sendYaMetrikaEvent(DOWNLOAD_MONETIZED_GOALS.contentLockDownloadTargetContentOnTimeout[this.browserType]);
            this._clickDownloadFile();
          });
        }
      }, 1000);

      this.timerIds.push(timerId);
    });
  }

  _clearTimers() {
    this.timerIds.forEach(clearInterval);
    this.timerIds = [];
  }

  _bindEvents() {
    // при клике должен скачать софт
    this.toplineSoftLinkEls.forEach((el) => {
      el.addEventListener("click", this._boundHandleClose, { once: true });
    });

    this.modalLinkEl.addEventListener("click", this._boundHandleDownloadBrowser, { once: true });

    // целевое скачивание при закрытии
    this.closeModalButton.addEventListener("click", this._boundClickDownloadFile);
    this.modalBackground.addEventListener("click", this._boundClickDownloadFile);
  }

  _unbindEvents() {
    this.toplineSoftLinkEls.forEach((el) => {
      el.removeEventListener("click", this._boundHandleClose);
    });
    this.modalLinkEl.removeEventListener("click", this._boundHandleDownloadBrowser);
    this.closeModalButton.removeEventListener("click", this._boundClickDownloadFile);
    this.modalBackground.removeEventListener("click", this._boundClickDownloadFile);
  }

  /**
   * Общие элементы обновления: изображение, название, ссылка, имя в баннере, цель soft-link.
   */
  _updateCommonElements() {
    const imageSrc = "/images/icons/browser/";
    this.modalImageEl.src = `${imageSrc}${this.browserType}.png`;
    this.modalImageEl.alt = this.browserType;
    this.modalTitleEl.textContent = this.browserTitle;
    this.modalLinkEl.href = this.browserLink;
    this.toplineBrowserEls.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.textContent = this.browserTitle;
    });
    this.toplineSoftLinkEls.forEach((el) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const attr of this.buttonElem.attributes) {
        if (attr.name !== "class") {
          el.setAttribute(attr.name, attr.value);
        }
      }
      el.setAttribute("data-id", "content-lock-soft-link");
      if (this.goal) el.setAttribute("data-goal", this.goal);
    });
  }

  /**
   * Общие элементы очистки: изображение, название, ссылка, таймер, имя браузера в баннере.
   */
  _clearCommonElements() {
    this.modalImageEl.src = "";
    this.modalTitleEl.textContent = "";
    this.modalLinkEl.href = "#";
    this.toplineSecondEls.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.textContent = "10";
    });
    this.toplineBrowserEls.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.textContent = "";
    });
  }
}

export default ContentLockModal;
